/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * organizerTokens
 * ---------------
 * The organizer dashboard renders on a FIXED palette — "Dawn", taken from the
 * UpVibe Android app's design system (app/src/main/res/values/colors.xml,
 * theme id 1: cream + vivid coral). It deliberately does NOT follow the
 * app-wide ThemeContext (agni, rudra, …): organizer screens are a distinct
 * surface with their own identity, and mixing the two produced a rail whose
 * colour fought the content.
 *
 * Derived values follow the rules documented in colors.xml itself:
 *   dim = text @55%   mute = text @32%
 *   accent_soft = accent @12%   accent_shadow = accent @32%
 *   light-theme line = 10% black, line_soft = 5% black
 *   light-theme chrome_bg = white highlight for the glass edge
 *
 * The `rail*` entries are this app's addition: the glass sidebar needs its own
 * set so a future theme family can own the rail without touching content tokens.
 *
 * Spread ORGANIZER_TOKENS onto the shell element's style prop; every organizer
 * component reads them through var(--org-*) and hardcodes no colour.
 */

export const DAWN = {
  bg: '#F4EFE7',
  surface: '#FFFFFF',
  surfaceHi: '#EAE3D4',
  text: '#1A1410',
  accent: '#E66838',
  rec: '#D14530',
  success: '#1E8E4E',
};

const TEXT_RGB = '26, 20, 16';       // #1A1410
const ACCENT_RGB = '230, 104, 56';   // #E66838
const SUCCESS_RGB = '30, 142, 78';   // #1E8E4E

export const ORGANIZER_TOKENS = {
  '--org-bg': DAWN.bg,
  '--org-surface': DAWN.surface,
  '--org-surface-hi': DAWN.surfaceHi,
  '--org-text': DAWN.text,
  '--org-dim': `rgba(${TEXT_RGB}, 0.55)`,
  '--org-mute': `rgba(${TEXT_RGB}, 0.32)`,
  '--org-line': 'rgba(0, 0, 0, 0.10)',
  '--org-line-soft': 'rgba(0, 0, 0, 0.05)',
  '--org-accent': DAWN.accent,
  '--org-accent-soft': `rgba(${ACCENT_RGB}, 0.12)`,
  '--org-accent-shadow': `rgba(${ACCENT_RGB}, 0.32)`,
  '--org-on-accent': '#FFFFFF',
  '--org-rec': DAWN.rec,
  '--org-success': DAWN.success,
  // Same derivation rule the accent uses: soft = 12%, shadow = 32%. Added
  // because status badges need a success fill, and the dashboard never did.
  '--org-success-soft': `rgba(${SUCCESS_RGB}, 0.12)`,
  '--org-success-line': `rgba(${SUCCESS_RGB}, 0.38)`,
  // Soft fill for refusals, same 12% rule as the others; and an amber for
  // "answer first" states (a late check-in), which the palette had no word for.
  '--org-rec-soft': 'rgba(209, 69, 48, 0.12)',
  '--org-warn': '#8A5A00',
  '--org-warn-soft': 'rgba(200, 140, 0, 0.16)',
  '--org-chrome': 'rgba(255, 255, 255, 0.55)',
  '--org-rad-card': '18px',
  '--org-rad-btn': '11px',

  // Glass rail
  '--org-rail-glass':
    'linear-gradient(180deg, rgba(255,255,255,0.62), rgba(244,239,231,0.78) 62%, rgba(255,255,255,0.58))',
  '--org-rail-fg': DAWN.text,
  '--org-rail-dim': `rgba(${TEXT_RGB}, 0.55)`,
  '--org-rail-mute': `rgba(${TEXT_RGB}, 0.32)`,
  '--org-rail-line': 'rgba(0, 0, 0, 0.10)',
  '--org-rail-chrome': 'rgba(255, 255, 255, 0.55)',
  '--org-rail-hover': 'rgba(0, 0, 0, 0.04)',
  '--org-rail-active': DAWN.surface,
  '--org-rail-bar': DAWN.accent,
  '--org-rail-chip': DAWN.surface,
  '--org-rail-av': DAWN.surface,
  '--org-rail-out': DAWN.rec,
};

export default ORGANIZER_TOKENS;
